import { BlipsResponse, BlipV2 } from "@/types/home";
import type { Blip } from "../types";
import { apiService } from "./apiService";
import {
  AddCommentRequest,
  AddCommentResponse,
  AddReplyRequest,
  AddReplyResponse,
  CommentReplyResponse,
  CommentsApiResponse,
} from "@/components/blips/comments/types/comment";

/**
 * Transform Blip data to ProductCard format
 */
export const transformBlipToProductCard = (blip: BlipV2): BlipV2 => ({
  ...blip,
  blipId: blip.blipId || "",
  productName: blip.productName || "",
  thumbnail: blip.thumbnail || "",
  price: blip.price || 0,
  discount: blip.discount,
  _id: blip._id || "",
  blipUrl: blip.blipUrl || "",
});

export const blipService = {
  /**
   * Get all blips with optional pagination
   *
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<Blip[]> - List of blips
   *
   * @example
   * ```typescript
   * const blips = await blipService.getBlips(1, 10);
   * blips.forEach(blip => {
   *   console.log(`${blip.title} by ${blip.author}`);
   * });
   * ```
   */
  async getBlips(page: number = 1, limit: number = 20): Promise<Blip[]> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/blips?${params.toString()}`;
    return apiService.get<Blip[]>(url);
  },

  /**
   * Get a single blip by product ID and blip ID
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @returns Promise<Blip> - Blip details
   *
   * @example
   * ```typescript
   * const blip = await blipService.getBlip('product-123', 'blip-456');
   * console.log(blip.title); // "My Amazing Blip"
   * console.log(blip.content); // "This is the content..."
   * ```
   */
  async getBlip(productId: string, blipId: string): Promise<Blip> {
    if (!productId) {
      throw new Error("Product ID is required");
    }

    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}`;
    const response = await apiService.get<{ blips: Blip[] }>(url);

    if (!response.blips || response.blips.length === 0) {
      throw new Error("Blip not found");
    }

    return response.blips[0];
  },

  /**
   * Create a new blip
   *
   * @param blipData - Blip data (without id, createdAt, updatedAt)
   * @returns Promise<Blip> - Created blip
   *
   * @example
   * ```typescript
   * const newBlip = await blipService.createBlip({
   *   title: 'My First Blip',
   *   content: 'This is my first blip content!',
   *   author: 'John Doe'
   * });
   * ```
   */
  async createBlip(
    blipData: Omit<Blip, "id" | "createdAt" | "updatedAt">
  ): Promise<Blip> {
    if (!blipData.title?.trim()) {
      throw new Error("Blip title is required");
    }

    if (!blipData.content?.trim()) {
      throw new Error("Blip content is required");
    }

    const url = "/api/blips";
    return apiService.post<Blip>(url, blipData);
  },

  /**
   * Update an existing blip
   *
   * @param id - Blip ID
   * @param blipData - Blip data to update
   * @returns Promise<Blip> - Updated blip
   *
   * @example
   * ```typescript
   * const updatedBlip = await blipService.updateBlip('blip-123', {
   *   title: 'Updated Title',
   *   content: 'Updated content'
   * });
   * ```
   */
  async updateBlip(id: string, blipData: Partial<Blip>): Promise<Blip> {
    if (!id) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/blips/${id}`;
    return apiService.put<Blip>(url, blipData);
  },

  /**
   * Delete a blip
   *
   * @param id - Blip ID
   * @returns Promise<void>
   *
   * @example
   * ```typescript
   * await blipService.deleteBlip('blip-123');
   * ```
   */
  async deleteBlip(id: string): Promise<void> {
    if (!id) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/blips/${id}`;
    return apiService.delete<void>(url);
  },

  /**
   * Get blips by author
   *
   * @param author - Author name
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<Blip[]> - List of blips by author
   *
   * @example
   * ```typescript
   * const authorBlips = await blipService.getBlipsByAuthor('John Doe');
   * authorBlips.forEach(blip => {
   *   console.log(blip.title);
   * });
   * ```
   */
  async getBlipsByAuthor(
    author: string,
    page: number = 1,
    limit: number = 20
  ): Promise<Blip[]> {
    if (!author?.trim()) {
      throw new Error("Author name is required");
    }

    const params = new URLSearchParams({
      author: author.trim(),
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/blips/author?${params.toString()}`;
    return apiService.get<Blip[]>(url);
  },

  /**
   * Search blips by query
   *
   * @param query - Search query
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<Blip[]> - Search results
   *
   * @example
   * ```typescript
   * const searchResults = await blipService.searchBlips('react');
   * searchResults.forEach(blip => {
   *   console.log(`Found: ${blip.title}`);
   * });
   * ```
   */
  async searchBlips(
    query: string,
    page: number = 1,
    limit: number = 20
  ): Promise<Blip[]> {
    if (!query?.trim()) {
      throw new Error("Search query is required");
    }

    const params = new URLSearchParams({
      q: query.trim(),
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/blips/search?${params.toString()}`;
    return apiService.get<Blip[]>(url);
  },

  /**
   * Get top blips for the week
   *
   * @param limit - Number of blips to fetch (default: 6)
   * @returns Promise<BlipsResponse> - Response with top blips and metadata
   *
   * @example
   * ```typescript
   * const topBlips = await blipService.getTopBlips(6);
   * topBlips.blips.forEach(blip => {
   *   console.log(`${blip.productName} - ${blip.price}`);
   * });
   * ```
   */
  async getTopBlips(limit: number = 6): Promise<BlipsResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
    });

    const url = `/api/v2/blips/top?${params.toString()}`;
    return apiService.get<BlipsResponse>(url);
  },

  /**
   * Get blips from followed sellers
   *
   * Returns blips only from sellers that the authenticated buyer follows.
   * Requires buyer authentication.
   *
   * @param page - Page number (default: 1)
   * @param limit - Number of blips per page (default: 10)
   * @param sessionId - Optional session ID for maintaining seen blips
   * @returns Promise<BlipsResponse> - Response with following blips and metadata
   * @throws Error if user is not authenticated or authorized
   *
   * @example
   * ```typescript
   * // Get first page of following blips
   * const followingBlips = await blipService.getFollowingBlips(1, 10);
   * console.log(`Found ${followingBlips.totalBlips} blips from ${followingBlips.followedSellersCount} sellers`);
   *
   * // Handle pagination
   * if (followingBlips.currentPage < followingBlips.totalPages) {
   *   const nextPage = await blipService.getFollowingBlips(2, 10);
   * }
   *
   * // Handle empty state
   * if (followingBlips.blips.length === 0) {
   *   console.log(followingBlips.message); // "No blips from followed sellers"
   * }
   * ```
   */
  async getFollowingBlips(
    page: number = 1,
    limit: number = 10,
    sessionId?: string
  ): Promise<BlipsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (sessionId) {
      params.append("sessionId", sessionId);
    }

    const url = `/api/v2/blips/following?${params.toString()}`;

    try {
      return await apiService.get<BlipsResponse>(url);
    } catch (error) {
      // Handle authentication errors gracefully
      if (error instanceof Error) {
        // Check if it's an authentication error
        if (
          error.message.includes("Authentication") ||
          error.message.includes("Unauthorized")
        ) {
          throw new Error(
            "Please log in to view blips from sellers you follow"
          );
        }
      }
      throw error;
    }
  },

  /**
   * Get trending blips based on engagement metrics
   *
   * Returns blips with high engagement including views, likes, comments, and sales.
   * Uses a comprehensive scoring algorithm with time-based factors.
   *
   * @param page - Page number (default: 1)
   * @param limit - Number of blips per page (default: 10)
   * @param sessionId - Optional session ID for maintaining seen blips
   * @returns Promise<BlipsResponse> - Response with trending blips and metadata
   *
   * @example
   * ```typescript
   * const trendingBlips = await blipService.getTrendingBlips(1, 30);
   * console.log(`Found ${trendingBlips.totalBlips} trending blips`);
   * ```
   */
  async getTrendingBlips(
    page: number = 1,
    limit: number = 10,
    sessionId?: string
  ): Promise<BlipsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (sessionId) {
      params.append("sessionId", sessionId);
    }

    const url = `/api/v2/blips/trending?${params.toString()}`;
    return apiService.get<BlipsResponse>(url);
  },

  /**
   * Get product blips feed with pagination
   *
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10)
   * @returns Promise with blips array and pagination data
   */
  async getProductBlips(
    page: number = 1,
    limit: number = 10
  ): Promise<{
    blips: BlipV2[];
    currentPage: number;
    totalPages: number;
    totalBlips: number;
  }> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/products/blips?${params.toString()}`;
    return apiService.get<{
      blips: BlipV2[];
      currentPage: number;
      totalPages: number;
      totalBlips: number;
    }>(url);
  },

  /**
   * Get a specific blip by product ID and blip ID
   *
   * This endpoint returns detailed information about a single blip including:
   * - Blip metadata (URL, content type, engagement metrics)
   * - Product information
   * - Seller details
   * - Comments (first page)
   *
   * @param productId - The product ID
   * @param blipId - The blip ID
   * @returns Promise<BlipV2> - Transformed blip data in BlipV2 format
   * @throws Error if productId or blipId is missing
   * @throws Error if blip is not found
   *
   * @example
   * ```typescript
   * try {
   *   const blip = await blipService.getBlipById('product-123', 'blip-456');
   *   console.log(blip.name); // Product name
   *   console.log(blip.seller.storeName); // Seller store name
   *   console.log(blip.blipEngagement.likesCount); // Number of likes
   * } catch (error) {
   *   console.error('Failed to fetch blip:', error);
   * }
   * ```
   */

  async getBlipById(productId: string, blipId: string): Promise<BlipV2> {
    if (!productId) throw new Error("Product ID is required");
    if (!blipId) throw new Error("Blip ID is required");

    const url = `/api/products/${productId}/blips/${blipId}`;
    const response = await apiService.get<{ blips: BlipV2[] }>(url);

    if (!response.blips || response.blips.length === 0) {
      throw new Error("Blip not found");
    }

    return response.blips[0];
  },

  /**
   * Follow/Unfollow a seller
   *
   * @param sellerId - Seller ID to follow/unfollow
   * @returns Promise with follow data
   */
  async followSeller(sellerId: string): Promise<void> {
    if (!sellerId) {
      throw new Error("Seller ID is required");
    }

    const url = `/api/buyer/follow/${sellerId}`;
    return apiService.post<void>(url, {});
  },

  async unFollowSeller(sellerId: string): Promise<void> {
    if (!sellerId) {
      throw new Error("Seller ID is required");
    }

    const url = `/api/buyer/follow/${sellerId}`;
    return apiService.delete<void>(url, {});
  },

  /**
   * Like/Unlike a blip
   *
   * @param productId - Product ID
   * @param blipId - Blip ID to like/unlike
   * @returns Promise<void>
   */
  async likeBlip(productId: string, blipId: string): Promise<void> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/like`;
    return apiService.post<void>(url, {});
  },

  /**
   * Save a blip for the authenticated buyer
   *
   * Idempotent: saving an already-saved blip returns success with created: false.
   * Requires buyer authentication.
   *
   * @param blipId - Blip ID to save
   * @returns Promise with status, message, and created flag
   *
   * @example
   * ```typescript
   * const result = await blipService.saveBlip('blip-123');
   * console.log(result.message); // "Blip saved successfully" or "Blip already saved"
   * console.log(result.created); // true or false
   * ```
   */
  async saveBlip(blipId: string): Promise<{
    status: boolean;
    message: string;
    created: boolean;
  }> {
    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/v2/blips/${blipId}/save`;
    return apiService.post<{
      status: boolean;
      message: string;
      created: boolean;
    }>(url, {});
  },

  /**
   * Unsave a blip for the authenticated buyer
   *
   * Idempotent: unsaving a non-saved blip returns success with removed: false.
   * Requires buyer authentication.
   *
   * @param blipId - Blip ID to unsave
   * @returns Promise with status, message, and removed flag
   *
   * @example
   * ```typescript
   * const result = await blipService.unsaveBlip('blip-123');
   * console.log(result.message); // "Blip unsaved successfully" or "Blip was not saved"
   * console.log(result.removed); // true or false
   * ```
   */
  async unsaveBlip(blipId: string): Promise<{
    status: boolean;
    message: string;
    removed: boolean;
  }> {
    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/v2/blips/${blipId}/save`;
    return apiService.delete<{
      status: boolean;
      message: string;
      removed: boolean;
    }>(url, {});
  },

  /**
   * Check if a blip is saved by the authenticated buyer
   *
   * Requires buyer authentication.
   *
   * @param blipId - Blip ID to check
   * @returns Promise with status and isSaved flag
   *
   * @example
   * ```typescript
   * const result = await blipService.checkBlipSaved('blip-123');
   * console.log(result.isSaved); // true or false
   * ```
   */
  async checkBlipSaved(blipId: string): Promise<{
    status: boolean;
    isSaved: boolean;
  }> {
    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const url = `/api/v2/blips/${blipId}/saved`;
    return apiService.get<{
      status: boolean;
      isSaved: boolean;
    }>(url);
  },

  /**
   * Get saved blips for the authenticated buyer
   *
   * Returns the authenticated buyer's saved blips with pagination.
   * The blip items are shaped identically to other blips endpoints.
   * Requires buyer authentication.
   *
   * @param page - Page number (default: 1)
   * @param limit - Number of blips per page (default: 20)
   * @returns Promise<BlipsResponse> - Response with saved blips and metadata
   * @throws Error if user is not authenticated or authorized
   *
   * @example
   * ```typescript
   * // Get first page of saved blips
   * const savedBlips = await blipService.getSavedBlips(1, 20);
   * console.log(`Found ${savedBlips.totalBlips} saved blips`);
   *
   * // Handle pagination
   * if (savedBlips.currentPage < savedBlips.totalPages) {
   *   const nextPage = await blipService.getSavedBlips(2, 20);
   * }
   * ```
   */
  async getSavedBlips(
    page: number = 1,
    limit: number = 20
  ): Promise<BlipsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/v2/blips/saved?${params.toString()}`;

    try {
      return await apiService.get<BlipsResponse>(url);
    } catch (error) {
      // Handle authentication errors gracefully
      if (error instanceof Error) {
        // Check if it's an authentication error
        if (
          error.message.includes("Authentication") ||
          error.message.includes("Unauthorized")
        ) {
          throw new Error(
            "Please log in to view your saved blips"
          );
        }
      }
      throw error;
    }
  },

  /**
   * Get comments for a specific blip
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise<CommentsApiResponse> - Comments with pagination
   *
   * @example
   * ```typescript
   * const comments = await blipService.getBlipComments('product-123', 'blip-456', 1, 20);
   * console.log(`Found ${comments.pagination.total} comments`);
   * ```
   */
  async getBlipComments(
    productId: string,
    blipId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<CommentsApiResponse> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }

    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/products/${productId}/blips/${blipId}/comments?${params.toString()}`;
    return apiService.get<CommentsApiResponse>(url);
  },

  /**
   * Add a comment to a blip
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param comment - Comment text
   * @returns Promise<AddCommentResponse> - Response with success message and count
   *
   * @example
   * ```typescript
   * const result = await blipService.addBlipComment('product-123', 'blip-456', 'Great product!');
   * console.log(result.message); // "Comment added successfully"
   * ```
   */
  async addBlipComment(
    productId: string,
    blipId: string,
    comment: string
  ): Promise<AddCommentResponse> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!comment?.trim()) {
      throw new Error("Comment text is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments`;
    const body: AddCommentRequest = { comment: comment.trim() };
    return apiService.post<AddCommentResponse>(url, body);
  },

  /**
   * Like a comment on a blip
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID to like
   * @returns Promise<void>
   */
  async likeComment(
    productId: string,
    blipId: string,
    commentId: string
  ): Promise<void> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/like`;
    return apiService.post<void>(url, {});
  },

  /**
   * Unlike a comment on a blip
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID to unlike
   * @returns Promise<void>
   */
  async unlikeComment(
    productId: string,
    blipId: string,
    commentId: string
  ): Promise<void> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/like`;
    return apiService.delete<void>(url);
  },

  /**
   * Get replies for a specific comment
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID to get replies for
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 20)
   * @returns Promise with replies array and pagination
   */
  async getCommentReplies(
    productId: string,
    blipId: string,
    commentId: string,
    page: number = 1,
    limit: number = 20
  ): Promise<{
    replies: CommentReplyResponse[];
    pagination: {
      total: number;
      pages: number;
      currentPage: number;
      hasMore: boolean;
    };
  }> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }

    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/replies?${params.toString()}`;
    return apiService.get<{
      replies: CommentReplyResponse[];
      pagination: {
        total: number;
        pages: number;
        currentPage: number;
        hasMore: boolean;
      };
    }>(url);
  },

  /**
   * Add a reply to a comment
   * Note: API expects 'comment' field, not 'reply'
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID to reply to
   * @param reply - Reply text
   * @returns Promise<AddReplyResponse> - Response with success message and count
   */
  async addCommentReply(
    productId: string,
    blipId: string,
    commentId: string,
    reply: string
  ): Promise<AddReplyResponse> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }
    if (!reply?.trim()) {
      throw new Error("Reply text is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/replies`;
    // API expects 'comment' field, not 'reply'
    const body: AddReplyRequest = { comment: reply.trim() };
    return apiService.post<AddReplyResponse>(url, body);
  },

  /**
   * Like a reply on a comment
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID
   * @param replyId - Reply ID to like
   * @returns Promise<void>
   */
  async likeReply(
    productId: string,
    blipId: string,
    commentId: string,
    replyId: string
  ): Promise<void> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }
    if (!replyId) {
      throw new Error("Reply ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/replies/${replyId}/like`;
    return apiService.post<void>(url, {});
  },

  /**
   * Unlike a reply on a comment
   *
   * @param productId - Product ID
   * @param blipId - Blip ID
   * @param commentId - Comment ID
   * @param replyId - Reply ID to unlike
   * @returns Promise<void>
   */
  async unlikeReply(
    productId: string,
    blipId: string,
    commentId: string,
    replyId: string
  ): Promise<void> {
    if (!productId) {
      throw new Error("Product ID is required");
    }
    if (!blipId) {
      throw new Error("Blip ID is required");
    }
    if (!commentId) {
      throw new Error("Comment ID is required");
    }
    if (!replyId) {
      throw new Error("Reply ID is required");
    }

    const url = `/api/products/${productId}/blips/${blipId}/comments/${commentId}/replies/${replyId}/like`;
    return apiService.delete<void>(url);
  },
  // Add this new method to the blipService object, after getTopBlips:

    /**
     * Get new/latest blips
     *
     * @param page - Page number (default: 1)
     * @param limit - Number of blips to fetch (default: 10)
     * @param sessionId - Optional session ID for maintaining seen blips
     * @returns Promise<BlipsResponse> - Response with new blips and metadata
     *
     * @example
     * ```typescript
     * const newBlips = await blipService.getNewBlips(1, 10);
     * newBlips.blips.forEach(blip => {
     *   console.log(`${blip.productName} - ${blip.price}`);
     * });
     * ```
     */
    async getNewBlips(
      page: number = 1,
      limit: number = 10,
      sessionId?: string
    ): Promise<BlipsResponse> {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (sessionId) {
        params.append("sessionId", sessionId);
      }

      const url = `/api/v2/blips/new?${params.toString()}`;
      return apiService.get<BlipsResponse>(url);
    },
};
